// Initialize Telegram Analytics for tapps store listing
// Configure via VITE_TELEGRAM_ANALYTICS_TOKEN and VITE_TELEGRAM_ANALYTICS_APP_NAME env vars
import telegramAnalytics from '@telegram-apps/analytics';

// Configuration constants
const DEFAULT_CREDITS = 1; // Free first try for all users
const AD_REWARD_CREDITS = 10; // Credits earned per ad watch
const POLL_INTERVAL_MS = 3000;     // Poll every 3 seconds
const POLL_TIMEOUT_MS = 30000;     // Stop polling after 30 seconds
const SYNCING_OPACITY = '0.7';     // Opacity for credits display during sync

// Only initialize analytics if both token and appName are configured
const analyticsToken = import.meta.env.VITE_TELEGRAM_ANALYTICS_TOKEN;
const analyticsAppName = import.meta.env.VITE_TELEGRAM_ANALYTICS_APP_NAME;

if (analyticsToken && analyticsAppName) {
    try {
        telegramAnalytics.init({
            token: analyticsToken,
            appName: analyticsAppName,
        });
    } catch (error) {
        console.warn('Failed to initialize Telegram Analytics:', error);
    }
}

// Weight conversion factors (all values relative to grams)
const WEIGHT_UNITS = {
    mg: { name: 'Milligrams', factor: 0.001 },
    g: { name: 'Grams', factor: 1 },
    kg: { name: 'Kilograms', factor: 1000 },
    oz: { name: 'Ounces', factor: 28.3495 },
    lb: { name: 'Pounds', factor: 453.592 },
    st: { name: 'Stones', factor: 6350.29 },
    t: { name: 'Metric Tons', factor: 1000000 },
    ct: { name: 'Carats', factor: 0.2 }
};

// Initialize Telegram WebApp
function initTelegramWebApp() {
    const tg = window.Telegram.WebApp;
    tg.expand();
    tg.ready();
    return tg;
}

// Wait for Telegram SDK to be ready
let tg;
if (window.Telegram && window.Telegram.WebApp) {
    tg = initTelegramWebApp();
} else {
    // Wait for mock to load
    document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
            if (window.Telegram && window.Telegram.WebApp) {
                tg = initTelegramWebApp();
                initApp();
            }
        }, 100);
    });
}

// State management
const state = {
    credits: DEFAULT_CREDITS,
    isPro: false,  // Renamed from hasPro for consistency
    adsgramLoaded: false,
    isConverting: false,
    isWatchingAd: false,
    lastConversion: null,
    canWatchAd: false,
    adWatchReason: null,
    // Credit sync state
    isSyncing: false,
    pendingCredits: null, // Expected credits value for polling
    pollInterval: null,
    pollStartTime: null
};

// DOM elements
const elements = {
    weightInput: document.getElementById('weight-input'),
    unitSelect: document.getElementById('unit-select'),
    convertBtn: document.getElementById('convert-btn'),
    resultsSection: document.getElementById('results-section'),
    resultsGrid: document.getElementById('results-grid'),
    copyBtn: document.getElementById('copy-btn'),
    resetBtn: document.getElementById('reset-btn'),
    watchAdBtn: document.getElementById('watch-ad-btn'),
    buyCreditsBtn: document.getElementById('buy-credits-btn'),
    buyProBtn: document.getElementById('buy-pro-btn'),
    creditsCount: document.getElementById('credits-count'),
    creditsDisplay: document.querySelector('.credits-display'),
    creditsSection: document.querySelector('.credits-section'),
    proBadge: document.getElementById('pro-status'),
    loadingOverlay: document.getElementById('loading-overlay'),
    loadingText: document.getElementById('loading-text')
};

// Get user ID from Telegram
function getUserId() {
    return tg?.initDataUnsafe?.user?.id || null;
}

// Sync credits with server
async function syncCredits() {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, using localStorage fallback');
        const savedCredits = localStorage.getItem('weightconverter_credits');
        const savedPro = localStorage.getItem('weightconverter_pro');
        state.credits = savedCredits ? parseInt(savedCredits, 10) : DEFAULT_CREDITS;
        state.isPro = savedPro === 'true';
        updateCreditsDisplay();
        return;
    }

    try {
        const response = await fetch(`/api/get-user-status?userId=${userId}`);
        const data = await response.json();
        
        if (data.success) {
            state.credits = data.credits;
            state.isPro = data.isPro || false;
            updateCreditsDisplay();
            console.log(`Credits synced from server: ${state.credits}, Pro: ${state.isPro}`);
        } else {
            throw new Error(data.message || 'Failed to sync credits');
        }
    } catch (error) {
        console.error('Error syncing credits:', error);
        // Fallback to localStorage if server fails
        const savedCredits = localStorage.getItem('weightconverter_credits');
        const savedPro = localStorage.getItem('weightconverter_pro');
        state.credits = savedCredits ? parseInt(savedCredits, 10) : DEFAULT_CREDITS;
        state.isPro = savedPro === 'true';
        updateCreditsDisplay();
    }
}

// Initialize credits from server
async function initCredits() {
    await syncCredits();
}

// Stop credit polling
function stopCreditPolling() {
    if (state.pollInterval) {
        clearInterval(state.pollInterval);
        state.pollInterval = null;
    }
    state.isSyncing = false;
    state.pendingCredits = null;
    state.pollStartTime = null;
    updateCreditsDisplay();
}

// Poll credits once with timeout check
async function pollCreditsOnce() {
    // Check if polling has timed out
    if (state.pollStartTime && Date.now() - state.pollStartTime > POLL_TIMEOUT_MS) {
        console.log('Credit polling timed out after 30s');
        stopCreditPolling();
        return;
    }

    try {
        const userId = getUserId();
        if (!userId) {
            console.warn('No user ID for polling');
            stopCreditPolling();
            return;
        }

        const response = await fetch(`/api/get-user-status?userId=${userId}`);
        const data = await response.json();
        
        if (data.success) {
            const oldCredits = state.credits;
            const oldIsPro = state.isPro;
            
            state.credits = data.credits;
            state.isPro = data.isPro || false;
            
            // Check if expected state is reached
            let targetReached = false;
            
            if (state.pendingCredits === 'pro') {
                // Waiting for Pro status
                targetReached = state.isPro && !oldIsPro;
            } else if (typeof state.pendingCredits === 'number') {
                // Waiting for specific credit amount or increase
                targetReached = state.credits >= state.pendingCredits;
            } else {
                // Waiting for any change
                targetReached = state.credits !== oldCredits || state.isPro !== oldIsPro;
            }
            
            if (targetReached) {
                console.log('Credit sync complete:', { credits: state.credits, isPro: state.isPro });
                stopCreditPolling();
            }
            
            updateCreditsDisplay();
        }
    } catch (error) {
        console.error('Error polling credits:', error);
        // Continue polling on error
    }
}

// Start credit polling with expected value
// expectedCredits can be: number (wait for credit increase), 'pro' (wait for Pro status), or null (any change)
function startCreditPolling(expectedCredits = null) {
    // Stop any existing polling
    stopCreditPolling();
    
    state.isSyncing = true;
    state.pendingCredits = expectedCredits;
    state.pollStartTime = Date.now();
    
    // Poll immediately
    pollCreditsOnce();
    
    // Set up polling interval
    state.pollInterval = setInterval(pollCreditsOnce, POLL_INTERVAL_MS);
    
    updateCreditsDisplay();
}

// Helper to start polling with expected amount and reason
function startCreditPollingWithExpected(expectedAmount, reason) {
    console.log(`Starting credit polling: expecting ${expectedAmount}, reason: ${reason}`);
    startCreditPolling(expectedAmount);
}

// Update credits display
function updateCreditsDisplay() {
    if (state.isPro) {
        elements.creditsCount.textContent = '∞';
        elements.creditsDisplay.classList.add('pro-status');
        // Show PRO badge
        if (elements.proBadge) {
            elements.proBadge.style.display = 'inline-block';
        }
        // Hide the credits section for Pro users since they don't need to purchase more
        if (elements.creditsSection) {
            elements.creditsSection.classList.add('hidden');
        }
    } else {
        // Hide PRO badge
        if (elements.proBadge) {
            elements.proBadge.style.display = 'none';
        }
        // Show syncing indicator if polling
        if (state.isSyncing && typeof state.pendingCredits === 'number') {
            elements.creditsCount.textContent = `${state.credits} → ${state.pendingCredits}...`;
            elements.creditsCount.style.opacity = SYNCING_OPACITY;
        } else if (state.isSyncing) {
            elements.creditsCount.textContent = `${state.credits}...`;
            elements.creditsCount.style.opacity = SYNCING_OPACITY;
        } else {
            elements.creditsCount.textContent = state.credits;
            elements.creditsCount.style.opacity = '1';
        }
        elements.creditsDisplay.classList.remove('pro-status');
        // Show the credits section for non-Pro users
        if (elements.creditsSection) {
            elements.creditsSection.classList.remove('hidden');
        }
    }
    updateConvertButtonState();
    updateAdButtonState();
}

// Update convert button state
function updateConvertButtonState() {
    const value = parseFloat(elements.weightInput.value);
    const hasValidInput = !isNaN(value) && value > 0;
    const canConvert = state.isPro || state.credits >= 1;
    
    if (state.isPro) {
        elements.convertBtn.innerHTML = 'Convert <span class="credit-cost">(Pro - Free)</span>';
    } else {
        elements.convertBtn.innerHTML = 'Convert <span class="credit-cost">(1 Credit)</span>';
    }
    
    elements.convertBtn.disabled = !hasValidInput || !canConvert || state.isConverting;
}

// Check if user can watch ads
async function checkAdEligibility() {
    const userId = getUserId();
    if (!userId) {
        state.canWatchAd = false;
        state.adWatchReason = 'No user ID available';
        updateAdButtonState();
        return;
    }

    try {
        const response = await fetch(`/api/can-watch-ad?userId=${userId}`);
        const data = await response.json();
        
        if (data.success) {
            state.canWatchAd = data.canWatch;
            state.adWatchReason = data.reason || null;
            updateAdButtonState();
        }
    } catch (error) {
        console.error('Error checking ad eligibility:', error);
        state.canWatchAd = false;
        updateAdButtonState();
    }
}

// Update ad button state
function updateAdButtonState() {
    // Always enable the watch ad button (no restrictions)
    elements.watchAdBtn.disabled = false;
    elements.watchAdBtn.innerHTML = 'Watch Ad <span class="credit-reward">+10 Credits</span>';
}

// Save credits to server
async function saveCredits() {
    const userId = getUserId();
    if (!userId) {
        console.warn('No user ID available, saving to localStorage');
        localStorage.setItem('weightconverter_credits', state.credits.toString());
        localStorage.setItem('weightconverter_pro', state.isPro.toString());
        updateCreditsDisplay();
        return;
    }

    try {
        const response = await fetch('/api/update-credits', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                userId,
                amount: state.credits,
                operation: 'set'
            })
        });

        const data = await response.json();
        
        if (data.success) {
            console.log(`Credits saved to server: ${data.credits}`);
            updateCreditsDisplay();
        } else {
            throw new Error(data.message || 'Failed to save credits');
        }
    } catch (error) {
        console.error('Error saving credits:', error);
        // Fallback to localStorage if server fails
        localStorage.setItem('weightconverter_credits', state.credits.toString());
        localStorage.setItem('weightconverter_pro', state.isPro.toString());
        updateCreditsDisplay();
    }
}

// Add credits
async function addCredits(amount) {
    state.credits += amount;
    await saveCredits();
    tg.showAlert(`You earned ${amount} credit${amount > 1 ? 's' : ''}!`);
}

/**
 * Deduct credits from user balance and sync with server
 * This uses the 'subtract' operation which automatically tracks credit usage
 * for ad watch eligibility checking
 * @param {number} amount - Amount of credits to deduct
 * @returns {Promise<boolean>} True if deduction was successful
 */
async function deductCredits(amount) {
    if (state.credits >= amount) {
        state.credits -= amount;
        
        // Update on server using subtract operation (which tracks usage)
        const userId = getUserId();
        if (userId) {
            try {
                const response = await fetch('/api/update-credits', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId,
                        amount: amount,
                        operation: 'subtract'
                    })
                });
                
                const data = await response.json();
                if (data.success) {
                    console.log(`Credits deducted on server: ${data.credits}`);
                    state.credits = data.credits; // Sync with server response
                } else {
                    console.error('Failed to deduct credits on server:', data.message);
                }
                updateCreditsDisplay();
            } catch (error) {
                console.error('Error deducting credits on server:', error);
                // Fallback to localStorage
                localStorage.setItem('weightconverter_credits', state.credits.toString());
                updateCreditsDisplay();
            }
        } else {
            // No user ID, use localStorage
            localStorage.setItem('weightconverter_credits', state.credits.toString());
            updateCreditsDisplay();
        }
        
        return true;
    }
    return false;
}

// Load Adsgram SDK
function loadAdsgramSDK() {
    if (state.adsgramLoaded) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://sad.adsgram.ai/js/sad.min.js';
        script.onload = () => {
            state.adsgramLoaded = true;
            resolve();
        };
        script.onerror = () => reject(new Error('Failed to load Adsgram SDK'));
        document.head.appendChild(script);
    });
}

// Convert weight from one unit to grams
function toGrams(value, fromUnit) {
    return value * WEIGHT_UNITS[fromUnit].factor;
}

// Convert weight from grams to another unit
function fromGrams(grams, toUnit) {
    return grams / WEIGHT_UNITS[toUnit].factor;
}

// Format number for display
function formatNumber(num) {
    if (num === 0) return '0';
    
    // For very small numbers, use scientific notation
    if (Math.abs(num) < 0.0001) {
        return num.toExponential(4);
    }
    
    // For very large numbers, use scientific notation
    if (Math.abs(num) >= 1000000000) {
        return num.toExponential(4);
    }
    
    // For regular numbers, use appropriate decimal places
    if (Number.isInteger(num)) {
        return num.toLocaleString();
    }
    
    // Determine appropriate decimal places based on magnitude
    const absNum = Math.abs(num);
    let decimals;
    if (absNum >= 1000) {
        decimals = 2;
    } else if (absNum >= 1) {
        decimals = 4;
    } else {
        decimals = 6;
    }
    
    return parseFloat(num.toFixed(decimals)).toLocaleString(undefined, {
        maximumFractionDigits: decimals
    });
}

// Perform weight conversion
function convertWeight(value, fromUnit) {
    const grams = toGrams(value, fromUnit);
    const results = {};
    
    for (const [unit, data] of Object.entries(WEIGHT_UNITS)) {
        results[unit] = {
            value: fromGrams(grams, unit),
            name: data.name
        };
    }
    
    return results;
}

// Display conversion results
function displayResults(results, fromUnit) {
    elements.resultsGrid.innerHTML = '';
    
    // Sort units: put the source unit first, then others alphabetically
    const sortedUnits = Object.keys(results).sort((a, b) => {
        if (a === fromUnit) return -1;
        if (b === fromUnit) return 1;
        return WEIGHT_UNITS[a].name.localeCompare(WEIGHT_UNITS[b].name);
    });
    
    for (const unit of sortedUnits) {
        const result = results[unit];
        const item = document.createElement('div');
        item.className = 'result-item';
        
        const isSource = unit === fromUnit;
        
        item.innerHTML = `
            <span class="result-unit">${result.name} (${unit})</span>
            <span class="result-value">${formatNumber(result.value)}</span>
        `;
        
        if (isSource) {
            item.style.background = 'var(--tg-theme-button-color, #0066cc)';
            item.style.color = 'var(--tg-theme-button-text-color, #fff)';
            item.querySelector('.result-unit').style.color = 'rgba(255,255,255,0.8)';
            item.querySelector('.result-value').style.color = '#fff';
        }
        
        elements.resultsGrid.appendChild(item);
    }
    
    elements.resultsSection.style.display = 'block';
    state.lastConversion = results;
}

// Show Adsgram ad
async function showAdsgramAd() {
    // Set loading state
    state.isWatchingAd = true;
    elements.watchAdBtn.disabled = true;
    elements.watchAdBtn.classList.add('loading');
    const originalText = elements.watchAdBtn.innerHTML;
    elements.watchAdBtn.innerHTML = 'Loading Ad...';
    
    const resetButtonState = () => {
        state.isWatchingAd = false;
        elements.watchAdBtn.disabled = false;
        elements.watchAdBtn.classList.remove('loading');
        elements.watchAdBtn.innerHTML = originalText;
    };
    
    try {
        await loadAdsgramSDK();
        
        // Initialize Adsgram with your block ID
        const AdController = window.Adsgram.init({ blockId: "20074" });
        
        elements.watchAdBtn.innerHTML = 'Watching Ad...';
        
        AdController.show().then(async () => {
            // User watched the ad successfully
            resetButtonState();
            
            // Start polling BEFORE API call for optimistic updates
            const expectedCredits = state.credits + 10;
            startCreditPollingWithExpected(expectedCredits, 'ad_reward');
            
            // Record ad watch on server
            const userId = getUserId();
            if (userId) {
                try {
                    const response = await fetch('/api/record-ad-watch', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ userId })
                    });
                    
                    const data = await response.json();
                    if (data.success) {
                        // Stop polling on successful response and update from server
                        stopCreditPolling();
                        state.credits = data.newBalance;
                        updateCreditsDisplay();
                        await checkAdEligibility(); // Refresh eligibility after watching
                        tg.showAlert(`You earned ${data.creditsEarned} credits!`);
                    } else {
                        throw new Error(data.message);
                    }
                } catch (error) {
                    console.error('Error recording ad watch:', error);
                    // Keep polling as fallback
                    tg.showAlert('Failed to grant credits. Please try again.');
                }
            } else {
                // Fallback for local testing
                await addCredits(10);
                stopCreditPolling();
            }
        }).catch((result) => {
            resetButtonState();
            // User closed the ad or an error occurred
            if (result.error) {
                tg.showAlert('Failed to load ad. Please try again later.');
            } else {
                tg.showAlert('Ad was closed before completion.');
            }
        });
    } catch (error) {
        console.error('Adsgram error:', error);
        resetButtonState();
        // Fallback: give credit anyway for testing
        tg.showAlert('Ad system is not configured yet. Free credit granted for testing!');
        await addCredits(10);
    }
}

// Initialize app
async function initApp() {
    await initCredits();
    await checkAdEligibility();
    setupEventListeners();
    applyTheme();
}

// Setup event listeners
function setupEventListeners() {
    // Handle input changes
    elements.weightInput.addEventListener('input', updateConvertButtonState);
    elements.unitSelect.addEventListener('change', updateConvertButtonState);

    // Handle conversion
    elements.convertBtn.addEventListener('click', handleConversion);

    // Handle copy
    elements.copyBtn.addEventListener('click', handleCopy);

    // Handle reset
    elements.resetBtn.addEventListener('click', handleReset);

    // Handle watch ad button
    elements.watchAdBtn.addEventListener('click', () => {
        showAdsgramAd();
    });

    // Handle buy credits button
    elements.buyCreditsBtn.addEventListener('click', handlePurchaseCredits);

    // Handle buy Pro button
    if (elements.buyProBtn) {
        elements.buyProBtn.addEventListener('click', handlePurchasePro);
    }

    // Handle Enter key on input
    elements.weightInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !elements.convertBtn.disabled) {
            handleConversion();
        }
    });
}

// Apply Telegram theme
function applyTheme() {
    if (tg && tg.themeParams) {
        document.documentElement.style.setProperty('--tg-theme-bg-color', tg.themeParams.bg_color || '#ffffff');
        document.documentElement.style.setProperty('--tg-theme-text-color', tg.themeParams.text_color || '#000000');
        document.documentElement.style.setProperty('--tg-theme-hint-color', tg.themeParams.hint_color || '#999999');
        document.documentElement.style.setProperty('--tg-theme-button-color', tg.themeParams.button_color || '#3390ec');
        document.documentElement.style.setProperty('--tg-theme-button-text-color', tg.themeParams.button_text_color || '#ffffff');
        document.documentElement.style.setProperty('--tg-theme-secondary-bg-color', tg.themeParams.secondary_bg_color || '#f0f0f0');
    }

    // Log ready state
    console.log('Telegram WebApp initialized:', {
        version: tg ? tg.version : 'mock',
        platform: tg ? tg.platform : 'web',
        colorScheme: tg ? tg.colorScheme : 'light'
    });
}

// Handle conversion
async function handleConversion() {
    // Prevent multiple conversions
    if (state.isConverting) {
        console.log('Already converting, ignoring click');
        return;
    }
    
    const value = parseFloat(elements.weightInput.value);
    const fromUnit = elements.unitSelect.value;
    
    if (isNaN(value) || value <= 0) {
        if (tg) tg.showAlert('Please enter a valid weight value');
        return;
    }

    // Pro users don't need credits
    if (!state.isPro && state.credits < 1) {
        if (tg) tg.showAlert('Not enough credits! Watch an ad or purchase credits.');
        return;
    }

    // Set converting state
    state.isConverting = true;
    elements.convertBtn.disabled = true;
    elements.convertBtn.classList.add('loading');
    
    // Show loading briefly for UX feedback
    elements.loadingOverlay.style.display = 'flex';
    
    // Deduct credit only for non-Pro users
    let deducted = true;
    if (!state.isPro) {
        deducted = await deductCredits(1);
        if (!deducted) {
            state.isConverting = false;
            elements.convertBtn.disabled = false;
            elements.convertBtn.classList.remove('loading');
            elements.loadingOverlay.style.display = 'none';
            if (tg) tg.showAlert('Not enough credits!');
            return;
        }
        
        // Refresh ad eligibility after using a credit (tracking happens server-side in subtractCredits)
        await checkAdEligibility();
    }

    try {
        // Small delay for UX
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Perform conversion
        const results = convertWeight(value, fromUnit);
        
        // Display results
        displayResults(results, fromUnit);
        
        // Hide loading
        elements.loadingOverlay.style.display = 'none';
        state.isConverting = false;
        elements.convertBtn.classList.remove('loading');
        updateConvertButtonState();
        
    } catch (error) {
        console.error('Error during conversion:', error);
        
        // Reset UI state
        elements.loadingOverlay.style.display = 'none';
        state.isConverting = false;
        elements.convertBtn.classList.remove('loading');
        updateConvertButtonState();
        
        if (tg) tg.showAlert('Conversion failed. Your credit has been refunded.');
        
        // Refund credit on error (only if deducted)
        if (!state.isPro && deducted) {
            await addCredits(1);
        }
    }
}

// Handle copy to clipboard
async function handleCopy() {
    if (!state.lastConversion) {
        if (tg) tg.showAlert('No conversion results to copy');
        return;
    }
    
    // Build copy text
    const lines = [];
    const inputValue = elements.weightInput.value;
    const inputUnit = elements.unitSelect.value;
    
    lines.push(`Weight Conversion: ${inputValue} ${WEIGHT_UNITS[inputUnit].name}`);
    lines.push('---');
    
    for (const [unit, data] of Object.entries(state.lastConversion)) {
        lines.push(`${data.name}: ${formatNumber(data.value)} ${unit}`);
    }
    
    const copyText = lines.join('\n');
    
    try {
        await navigator.clipboard.writeText(copyText);
        
        // Visual feedback
        const originalText = elements.copyBtn.innerHTML;
        elements.copyBtn.innerHTML = 'Copied';
        elements.copyBtn.classList.add('copied');
        
        setTimeout(() => {
            elements.copyBtn.innerHTML = originalText;
            elements.copyBtn.classList.remove('copied');
        }, 2000);
        
    } catch (error) {
        console.error('Failed to copy:', error);
        if (tg) tg.showAlert('Failed to copy to clipboard');
    }
}

// Handle reset
function handleReset() {
    // Reset state
    state.lastConversion = null;
    state.isConverting = false;
    
    // Reset inputs
    elements.weightInput.value = '';
    elements.unitSelect.selectedIndex = 0;
    
    // Hide results
    elements.resultsSection.style.display = 'none';
    elements.resultsGrid.innerHTML = '';
    
    // Reset button states
    elements.convertBtn.classList.remove('loading');
    elements.copyBtn.classList.remove('copied');
    
    // Update button state
    updateConvertButtonState();
    
    // Hide loading if visible
    elements.loadingOverlay.style.display = 'none';
    
    // Focus input
    elements.weightInput.focus();
}

// Handle purchase credits
async function handlePurchaseCredits() {
    if (!tg || !tg.openInvoice) {
        if (tg) tg.showAlert('Payment feature is not available in this environment.');
        return;
    }

    // Show loading state
    elements.buyCreditsBtn.disabled = true;
    elements.buyCreditsBtn.innerHTML = 'Creating invoice...';

    try {
        // Get user ID from Telegram WebApp
        const userId = tg.initDataUnsafe?.user?.id || null;

        // Call serverless function to create invoice
        const response = await fetch('/api/create-invoice', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to create invoice');
        }

        // Open Telegram invoice
        tg.openInvoice(data.invoiceLink, (status) => {
            if (status === 'paid') {
                // Start polling for credit increase
                const expectedCredits = state.credits + 10;
                startCreditPollingWithExpected(expectedCredits, 'credit_purchase');
                if (tg) tg.showAlert('Payment successful! Credits will be added shortly.');
            } else if (status === 'cancelled') {
                if (tg) tg.showAlert('Payment was cancelled.');
            } else if (status === 'failed') {
                if (tg) tg.showAlert('Payment failed. Please try again.');
            }
        });
    } catch (error) {
        console.error('Error creating invoice:', error);
        if (tg) tg.showAlert(`Failed to create payment: ${error.message}`);
    } finally {
        // Reset button state
        elements.buyCreditsBtn.disabled = false;
        elements.buyCreditsBtn.innerHTML = 'Buy 10 Credits <span class="credit-cost">(5 Stars)</span>';
    }
}

// Handle purchase Unlimited Credits
async function handlePurchasePro() {
    if (!tg || !tg.openInvoice) {
        if (tg) tg.showAlert('Payment feature is not available in this environment.');
        return;
    }

    // Check if user already has Unlimited Credits
    if (state.isPro) {
        if (tg) tg.showAlert('You already have Unlimited Credits!');
        return;
    }

    // Show loading state
    elements.buyProBtn.disabled = true;
    const originalText = elements.buyProBtn.innerHTML;
    elements.buyProBtn.innerHTML = 'Creating invoice...';

    try {
        // Get user ID from Telegram WebApp
        const userId = tg.initDataUnsafe?.user?.id || null;

        // Call serverless function to create invoice for Unlimited Credits
        const response = await fetch('/api/create-pro-invoice', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ userId })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to create invoice');
        }

        // Open Telegram invoice
        tg.openInvoice(data.invoiceLink, async (status) => {
            if (status === 'paid') {
                // Start polling with 'pro' expectation
                startCreditPolling('pro');
                if (tg) tg.showAlert('Payment successful! Unlimited Credits will be activated shortly.');
            } else if (status === 'cancelled') {
                if (tg) tg.showAlert('Payment was cancelled.');
            } else if (status === 'failed') {
                if (tg) tg.showAlert('Payment failed. Please try again.');
            }
        });
    } catch (error) {
        console.error('Error creating invoice for Unlimited Credits:', error);
        if (tg) tg.showAlert(`Failed to create payment: ${error.message}`);
    } finally {
        // Reset button state
        elements.buyProBtn.disabled = false;
        elements.buyProBtn.innerHTML = originalText;
    }
}

// Start app if Telegram is already loaded
if (tg) {
    initApp();
}
